/**
 * API Documentation - Enterprise Dashboard
 * OpenAPI documentation + API keys management (real backend data only)
 */

import { useState } from 'react'
import { useAuthContext } from '@/contexts/AuthContext'
import { AdminLayout } from '../../layout/AdminLayout'
import { EnterprisePageHeader } from '../../components/layout/EnterprisePageHeader'
import { EnterpriseEmptyState } from '../../components/layout/EnterpriseEmptyState'
import { EnterpriseSectionSkeleton } from '../../components/layout/EnterpriseSectionSkeleton'
import { EnterpriseStickyActionBar } from '../../components/layout/EnterpriseStickyActionBar'
import { useApiDocumentation } from '@/hooks/useApiDocumentation'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { Input } from '@/shared/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { toast } from '@/shared/hooks/use-toast'
import { 
  FileText, 
  Key,
  Copy,
  Plus,
  Trash2,
  Shield,
  Code,
  Activity,
  AlertCircle,
  Eye
} from 'lucide-react'

export default function ApiDocumentation() {
  const { user, logout } = useAuthContext()
  const {
    endpoints,
    apiKeys,
    availableScopes,
    isLoading,
    error,
    createApiKey,
    revokeApiKey,
    refresh
  } = useApiDocumentation()

  const [selectedEndpoint, setSelectedEndpoint] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(['server:read'])
  const [newKeyExpiresDays, setNewKeyExpiresDays] = useState<number>(365)
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const handleLogout = () => {
    void logout()
  }

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: 'Nome chiave mancante',
        description: 'Inserisci un nome per creare la API key.',
        variant: 'destructive'
      })
      return
    }
    
    setActionLoading('create-key')
    try {
      const result = await createApiKey(newKeyName.trim(), newKeyScopes, newKeyExpiresDays)
      if (result.success) {
        setNewKeyName('')
        setCreatedApiKey(result.api_key || null)
        toast({
          title: 'API key creata',
          description: 'La nuova chiave e stata generata correttamente.'
        })
      } else {
        toast({
          title: 'Creazione chiave fallita',
          description: result.error || 'Errore sconosciuto',
          variant: 'destructive'
        })
      }
    } finally {
      setActionLoading(null)
    }
  }

  const handleRevokeKey = async (keyId: number) => {
    setActionLoading(`revoke-${keyId}`)
    try {
      const result = await revokeApiKey(keyId)
      toast({
        title: result.success ? 'API key revocata' : 'Revoca fallita',
        description: result.success
          ? 'La chiave e stata disattivata.'
          : result.error || 'Errore sconosciuto',
        variant: result.success ? 'default' : 'destructive'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const getMethodBadge = (method: string) => {
    const colors = {
      GET: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      POST: 'bg-green-500/10 text-green-500 border-green-500/20',
      PUT: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      DELETE: 'bg-red-500/10 text-red-500 border-red-500/20',
      PATCH: 'bg-purple-500/10 text-purple-500 border-purple-500/20'
    }
    return <Badge className={colors[method as keyof typeof colors] || 'bg-gray-500/10 text-gray-500 border-gray-500/20'}>{method}</Badge>
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive 
      ? <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>
      : <Badge variant="destructive">Revoked</Badge>
  }

  const filteredEndpoints = endpoints.filter(endpoint => {
    const matchesSearch = endpoint.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(endpoint.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTag = !selectedTag || endpoint.tags.includes(selectedTag)
    return matchesSearch && matchesTag
  })

  const allTags = Array.from(new Set(endpoints.flatMap(e => e.tags)))
  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6 sm:space-y-8">
        <EnterprisePageHeader
          badge="API"
          title="API Documentation"
          subtitle="Documentazione completa API con gestione chiavi e analytics"
          icon={<FileText className="h-6 w-6" />}
          isLoading={isLoading}
          onRefresh={refresh}
        />

        {/* API Overview */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-6">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-500" />
                  Scopes
                </div>
                <Badge variant="outline" className="text-xs">Available</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">
                {availableScopes.length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                used to create API keys
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-green-500" />
                  API Keys
                </div>
                <Badge variant="outline" className="text-xs">Active</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">
                {apiKeys.filter(k => k.is_active).length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {apiKeys.length} total keys
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-orange-500" />
                  OpenAPI Tags
                </div>
                <Badge variant="outline" className="text-xs">Categories</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">
                {allTags.length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                grouped by router tags
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-500" />
                  Endpoints
                </div>
                <Badge variant="outline" className="text-xs">Available</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl sm:text-3xl font-bold text-foreground">{endpoints.length}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {allTags.length} categories
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {/* API Endpoints */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5 text-primary" />
                API Endpoints ({filteredEndpoints.length})
              </CardTitle>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  placeholder="Search endpoints..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <select
                  className="px-3 py-2 border border-input bg-background rounded-md text-sm"
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                >
                  <option value="">All Tags</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <EnterpriseStickyActionBar position="top">
                <span className="text-xs text-muted-foreground">
                  {filteredEndpoints.length} endpoint filtrati
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearchTerm('')
                    setSelectedTag('')
                  }}
                  className="ml-auto"
                >
                  Reset filtri
                </Button>
              </EnterpriseStickyActionBar>
              <ScrollArea className="h-72 sm:h-96">
                {isLoading ? (
                  <EnterpriseSectionSkeleton rows={7} showHeader={false} rowClassName="h-20" />
                ) : filteredEndpoints.length > 0 ? (
                  <div className="space-y-2">
                    {filteredEndpoints.map((endpoint, index) => (
                      <div
                        key={index}
                        className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          selectedEndpoint === endpoint 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                        onClick={() => setSelectedEndpoint(endpoint)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getMethodBadge(endpoint.method)}
                            <code className="text-sm font-mono">{endpoint.path}</code>
                          </div>
                          {Array.isArray((endpoint as any).security) && (endpoint as any).security.length > 0 && (
                            <Shield className="h-4 w-4 text-yellow-500" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                        {endpoint.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {endpoint.tags.map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <EnterpriseEmptyState
                    icon={<Code className="h-8 w-8 opacity-50" />}
                    title="Nessun endpoint trovato"
                    description="Modifica ricerca o tag per vedere altre rotte."
                  />
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* API Keys & Details */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                API Keys
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="keys" className="w-full">
                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3">
                  <TabsTrigger value="keys" className="text-xs sm:text-sm">Keys</TabsTrigger>
                  <TabsTrigger value="scopes" className="text-xs sm:text-sm">Scopes</TabsTrigger>
                  <TabsTrigger value="details" className="text-xs sm:text-sm">Details</TabsTrigger>
                </TabsList>

                <TabsContent value="keys" className="space-y-4">
                  <EnterpriseStickyActionBar position="top">
                    <Input
                      placeholder="API Key name..."
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      className="min-w-[220px] flex-1"
                    />
                    <Button 
                      onClick={handleCreateApiKey}
                      disabled={!newKeyName.trim() || actionLoading === 'create-key'}
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crea Key
                    </Button>
                  </EnterpriseStickyActionBar>

                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-500" />
                      Scopes
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {availableScopes.map((s) => {
                        const checked = newKeyScopes.includes(s.name)
                        return (
                          <label key={s.name} className="flex items-start gap-2 text-sm cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? Array.from(new Set([...newKeyScopes, s.name]))
                                  : newKeyScopes.filter((x) => x !== s.name)
                                setNewKeyScopes(next.length > 0 ? next : ['server:read'])
                              }}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-mono">{s.name}</span>
                              <span className="text-muted-foreground"> {s.description}</span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Expires (days)</span>
                      <Input
                        type="number"
                        min={1}
                        max={3650}
                        value={String(newKeyExpiresDays)}
                        onChange={(e) => setNewKeyExpiresDays(Math.max(1, Math.min(3650, Number(e.target.value || 365))))}
                        className="w-28"
                      />
                    </div>
                  </div>

                  {createdApiKey && (
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm dark:border-yellow-900/40 dark:bg-yellow-950/20">
                      <div className="flex items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="font-medium">API key created (shown only once)</div>
                          <code className="block break-all bg-background/60 px-2 py-1 rounded font-mono text-xs">{createdApiKey}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(createdApiKey)
                                toast({ title: 'API key copiata negli appunti' })
                              } catch (e) {
                                toast({
                                  title: 'Copia fallita',
                                  description: e instanceof Error ? e.message : 'Clipboard non disponibile',
                                  variant: 'destructive'
                                })
                              }
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setCreatedApiKey(null)}>
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <ScrollArea className="h-64">
                    {isLoading ? (
                      <EnterpriseSectionSkeleton rows={5} showHeader={false} rowClassName="h-20" />
                    ) : apiKeys.length > 0 ? (
                      <div className="space-y-2">
                        {apiKeys.map((apiKey) => (
                          <div key={apiKey.id} className="p-3 border rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Key className="h-4 w-4" />
                                <span className="font-medium text-sm">{apiKey.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(apiKey.is_active)}
                                {apiKey.is_active && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRevokeKey(apiKey.id)}
                                    disabled={actionLoading === `revoke-${apiKey.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{apiKey.key_prefix}</code>
                                {apiKey.expires_at && (
                                  <span className="text-xs text-muted-foreground">
                                    expires {new Date(apiKey.expires_at).toLocaleDateString('it-IT')}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                scopes: {apiKey.scopes.length > 0 ? apiKey.scopes.join(', ') : 'none'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EnterpriseEmptyState
                        icon={<Key className="h-8 w-8 opacity-50" />}
                        title="Nessuna API key creata"
                        description="Crea una chiave per automatizzare integrazioni e pannelli esterni."
                      />
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="scopes" className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    Available scopes are returned by the backend and used to create API keys.
                  </div>
                  <ScrollArea className="h-64">
                    <div className="space-y-2">
                      {availableScopes.map((s) => (
                        <div key={s.name} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <code className="text-sm font-mono">{s.name}</code>
                            <Badge variant="outline" className="text-xs">scope</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">{s.description}</div>
                        </div>
                      ))}
                      {availableScopes.length === 0 && (
                        <EnterpriseEmptyState
                          icon={<Shield className="h-8 w-8 opacity-50" />}
                          title="Nessuno scope disponibile"
                          description="Il backend non ha restituito scope configurati."
                        />
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="details" className="space-y-4">
                  {selectedEndpoint ? (
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {getMethodBadge(selectedEndpoint.method)}
                          <code className="font-mono">{selectedEndpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{selectedEndpoint.description}</p>
                      </div>

                      {Array.isArray((selectedEndpoint as any).security) && (selectedEndpoint as any).security.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Security</h4>
                          <div className="space-y-2">
                            {(selectedEndpoint as any).security.map((sec: any, index: number) => (
                              <div key={index} className="p-2 bg-muted/50 rounded text-xs">
                                <span className="text-muted-foreground">requirements: </span>
                                <code className="font-mono">
                                  {Object.keys(sec || {}).join(', ') || 'unknown'}
                                </code>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Parameters</h4>
                          <div className="space-y-2">
                            {selectedEndpoint.parameters.map((param: any, index: number) => (
                              <div key={index} className="p-2 bg-muted/50 rounded">
                                <div className="flex items-center gap-2">
                                  <code className="text-sm">{param.name}</code>
                                  <Badge variant="outline" className="text-xs">{param.in}</Badge>
                                  {param.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{param.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <h4 className="font-semibold mb-2">Responses</h4>
                        <div className="space-y-2">
                          {selectedEndpoint.responses.map((response: any, index: number) => (
                            <div key={index} className="p-2 bg-muted/50 rounded">
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant={response.status < 400 ? 'default' : 'destructive'}
                                  className="text-xs"
                                >
                                  {response.status}
                                </Badge>
                                <span className="text-sm">{response.description}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EnterpriseEmptyState
                      icon={<Eye className="h-8 w-8 opacity-50" />}
                      title="Seleziona un endpoint"
                      description="Scegli una rotta nella lista per vedere parametri, sicurezza e risposte."
                    />
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Errore di connessione</span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
